import "./styles.scss";

import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import CustomModal from "@components/CustomModal";
import { Text, Button, StatusChip } from "@fragments";
import {
  IconCloudUpload,
  IconFileSpreadsheet,
  IconTrash,
  IconDownload,
  IconUpload,
} from "@tabler/icons-react";

import api from "@helpers/api";

const ACCEPT = ".xlsx,.xls,.csv";
const VALID_EXTENSIONS = ["xlsx", "xls", "csv"];
const MAX_BYTES = 5 * 1024 * 1024;

// Client-side .xlsx template (header + sample row) so the owner can fill the
// expected columns offline. Built with SheetJS, matching the import format.
const TEMPLATE_ROWS = [
  ["email", "name", "phone"],
  ["exemplo@email.com", "Nome do criador", "+5511999999999"],
];
const TEMPLATE_FILENAME = "modelo-importacao-mentorados.xlsx";

/**
 * Bulk import — §6.6. Imperative open: modalRef.current.open(program, onDone).
 * Drop zone (.xlsx/.xls/.csv, ≤5MB) → multipart FormData → 202 (async). The
 * file is sent as-is (NOT the presigned-S3 flow) since the endpoint ingests it.
 */
export default function ImportModal({ modalRef, communityId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const inputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const programRef = useRef(null);
  const onDoneRef = useRef(null);

  modalRef.current = {
    open(program, onDone) {
      programRef.current = program ?? null;
      onDoneRef.current = onDone;
      setFile(null);
      setDragOver(false);
      dragDepthRef.current = 0;
      setOpen(true);
    },
  };

  const program = programRef.current;

  function selectFile(selected) {
    if (!selected) return;
    const ext = selected.name.split(".").pop()?.toLowerCase();
    if (!VALID_EXTENSIONS.includes(ext)) {
      window.setSnackbar?.("Formato inválido. Use .xlsx, .xls ou .csv.");
      return;
    }
    if (selected.size > MAX_BYTES) {
      window.setSnackbar?.("Arquivo muito grande (máximo 5MB).");
      return;
    }
    setFile(selected);
  }

  function handleInputChange(event) {
    selectFile(event?.target?.files?.[0]);
    if (event?.target) event.target.value = null;
  }

  function handleDrop(event) {
    event.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    selectFile(event.dataTransfer?.files?.[0]);
  }

  function handleDragEnter(event) {
    event.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  }

  function handleDragLeave(event) {
    event.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragOver(false);
    }
  }

  function downloadTemplate() {
    const sheet = XLSX.utils.aoa_to_sheet(TEMPLATE_ROWS);
    sheet["!cols"] = [{ wch: 28 }, { wch: 24 }, { wch: 20 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, "Mentorados");
    XLSX.writeFile(book, TEMPLATE_FILENAME);
  }

  async function handleSubmit() {
    if (!file) {
      window.setSnackbar?.("Selecione um arquivo para importar.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("file", file);

    const res = await api.post(
      `community/${communityId}/mentoring/programs/${program?.id}/import`,
      formData,
    );

    setLoading(false);

    if (res?.response?.status >= 400) {
      window.setSnackbar?.(res.response.data?.message || "Arquivo inválido ou ausente");
      return;
    }

    window.setSnackbar?.(
      "Importação iniciada — você receberá um e-mail com o resultado",
    );
    onDoneRef.current?.();
    setOpen(false);
  }

  return (
    <CustomModal
      maxWidth={560}
      open={open}
      handleClose={() => setOpen(false)}
      title="Importar mentorados"
      bottomButtonSumbitText="Importar"
      bottomButtonSumbitIcon={<IconUpload size={18} />}
      handleSubmit={handleSubmit}
      loading={loading}
      disabled={!file}
      bodyComponent={
        open ? (
          <div className="import-modal">
            <Text.span contrast={6}>
              Envie uma planilha com os mentorados que deseja inscrever no
              programa{program ? ` "${program.name}"` : ""}. Cada linha gera um
              convite por e-mail.
            </Text.span>

            <button
              type="button"
              className="import-template"
              onClick={downloadTemplate}
            >
              <span className="import-template-icon">
                <IconFileSpreadsheet size={18} />
              </span>
              <span className="import-template-text">
                <Text.span contrast={8} weight={500}>
                  Baixar modelo da planilha
                </Text.span>
                <Text.span contrast={5}>
                  Colunas: email (obrigatório) · name · phone
                </Text.span>
              </span>
              <IconDownload size={16} className="import-template-chevron" />
            </button>

            {file ? (
              <div className="import-file">
                <span className="import-file-icon">
                  <IconFileSpreadsheet size={20} />
                </span>
                <div className="import-file-details">
                  <Text.h6 contrast={8} weight={500} maxLines={1}>
                    {file.name}
                  </Text.h6>
                  <div className="import-file-meta">
                    <StatusChip.Default text="Pronto para enviar" />
                    <Text.span contrast={5}>{formatBytes(file.size)}</Text.span>
                  </div>
                </div>
                <Button
                  color="overlay"
                  size="small"
                  icon={<IconTrash size={16} />}
                  tooltip="Remover arquivo"
                  onClick={() => setFile(null)}
                  preventLoading
                />
              </div>
            ) : (
              <div
                className={`import-dropzone${dragOver ? " is-drag-over" : ""}`}
                onClick={() => inputRef.current?.click()}
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                role="button"
                tabIndex={0}
              >
                <span className="import-dropzone-icon">
                  <IconCloudUpload size={26} />
                </span>
                <Text.h6 contrast={8} weight={500}>
                  Arraste a planilha ou clique para selecionar
                </Text.h6>
                <Text.span contrast={5}>
                  .xlsx, .xls ou .csv · até 5MB
                </Text.span>
                <input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPT}
                  onChange={handleInputChange}
                  className="import-dropzone-input"
                />
              </div>
            )}
          </div>
        ) : null
      }
    />
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB"];
  let size = bytes;
  let i = 0;
  while (size >= 1024 && i < units.length - 1) {
    size /= 1024;
    i += 1;
  }
  return `${size.toFixed(size >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}
